import { useState, useEffect } from 'react'
import { useFaturas } from '@/hooks/useFaturas'
import { faturaService } from '@/services/fatura.service'
import { formatCurrency, formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import { Receipt, Calendar, CheckCircle2, Clock, AlertTriangle, Copy, Check, ExternalLink } from 'lucide-react'

import { usePortalStore } from '@/stores/portal.store'

export function PortalFaturasPage() {
    const associado = usePortalStore(s => s.associado)
    const PORTAL_ASSOCIADO_ID = associado?.id || 'a1'

    const rawCpf = usePortalStore(s => s.cpf)
    const portalCpf = rawCpf || associado?.cpf || ''
    const { data: result, isLoading } = useFaturas({ cpf: portalCpf, pageSize: 50 })
    const faturas = (result?.data || []).sort((a, b) => b.data_vencimento.localeCompare(a.data_vencimento))
    const [expandedId, setExpandedId] = useState<string | null>(null)
    const [isSyncing, setIsSyncing] = useState(false)

    useEffect(() => {
        if (portalCpf) {
            setIsSyncing(true)
            faturaService.syncAsaas({ cpf: portalCpf })
                .finally(() => setIsSyncing(false))
        }
    }, [portalCpf])

    const pendentes = faturas.filter(f => (f.status === 'pendente' || f.status === 'atrasada') && !(f as any).deleted_at)
    const pagas = faturas.filter(f => f.status === 'paga' && !(f as any).deleted_at)
    const canceladas = faturas.filter(f => f.status === 'cancelada' || (f as any).deleted_at)

    return (
        <div className="p-4 space-y-5 animate-fade-in max-w-lg mx-auto">
            {/* ... cabeçalho ... */}
            <div className="pt-2 flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold text-surface-900">Minhas Faturas</h1>
                    <p className="text-sm text-surface-700/60">{faturas.length} fatura{faturas.length !== 1 ? 's' : ''}</p>
                </div>
                {isSyncing && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 animate-pulse">
                        <div className="w-2 h-2 rounded-full bg-blue-600" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Sincronizando Asaas</span>
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : (
                <>
                    {/* Pendentes */}
                    {pendentes.length > 0 && (
                        <section className="space-y-2">
                            <p className="text-xs font-medium text-surface-700/60 uppercase flex items-center gap-1.5">
                                <Clock size={12} /> Pendentes / Atrasadas
                            </p>
                            {pendentes.map(f => (
                                <FaturaCard key={f.id} fatura={f} expanded={expandedId === f.id}
                                    onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)} />
                            ))}
                        </section>
                    )}

                    {/* Pagas */}
                    {pagas.length > 0 && (
                        <section className="space-y-2">
                            <p className="text-xs font-medium text-surface-700/60 uppercase flex items-center gap-1.5">
                                <CheckCircle2 size={12} /> Pagas
                            </p>
                            {pagas.map(f => (
                                <FaturaCard key={f.id} fatura={f} expanded={expandedId === f.id}
                                    onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)} />
                            ))}
                        </section>
                    )}

                    {/* Canceladas / Excluídas */}
                    {canceladas.length > 0 && (
                        <section className="space-y-2">
                            <p className="text-xs font-medium text-surface-700/40 uppercase flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-surface-300" /> Canceladas / Excluídas
                            </p>
                            {canceladas.map(f => (
                                <FaturaCard key={f.id} fatura={f} expanded={expandedId === f.id}
                                    onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)} />
                            ))}
                        </section>
                    )}

                    {faturas.length === 0 && (
                        <div className="text-center py-12">
                            <Receipt size={40} className="text-surface-700/20 mx-auto mb-3" />
                            <p className="text-surface-700/50">Nenhuma fatura encontrada</p>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

function FaturaCard({ fatura, expanded, onToggle }: {
    fatura: { id: string; valor: number; data_vencimento: string; data_pagamento: string | null; status: string; tipo: string; contrato?: any; pix_copia_cola?: string | null; url_boleto?: string | null; url_pix?: string | null; asaas_invoice_url?: string | null; metadata?: any; asaas_empresa?: string }
    expanded: boolean
    onToggle: () => void
}) {
    const f = fatura
    const isDeleted = !!(f as any).deleted_at
    const isPaga = f.status === 'paga' && !isDeleted
    const isAtrasada = f.status === 'atrasada' && !isDeleted
    const [copied, setCopied] = useState(false)

    const handleCopyPix = () => {
        navigator.clipboard.writeText('00020126580014br.gov.bcb.pix0136demo-pix-key-placeholder5204000053039865802BR5925PROTECAO VEICULAR SP6009SAO PAULO62070503***6304ABCD')
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className={`rounded-2xl bg-white border shadow-sm overflow-hidden transition-colors
            ${isAtrasada ? 'border-danger-200' : 'border-surface-100'}`}>
            <button onClick={onToggle} className="w-full p-4 flex items-center gap-3 text-left">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                    ${isPaga ? 'bg-success-100' : isAtrasada ? 'bg-danger-100' : 'bg-warning-100'}`}>
                    {isPaga ? <CheckCircle2 size={20} className="text-success-600" /> :
                        isAtrasada ? <AlertTriangle size={20} className="text-danger-600" /> :
                            <Clock size={20} className="text-warning-600" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                        <p className="font-bold text-surface-900">{formatCurrency(f.valor)}</p>
                        <span className={`badge ${isDeleted ? 'bg-surface-100 text-surface-500' : getStatusBadgeClass(f.status)}`}>
                            {isDeleted ? 'Cancelada' : translateStatus(f.status)}
                        </span>
                    </div>

                    <p className="text-sm font-bold text-surface-900 mt-1 line-clamp-1">
                        <span className="text-primary-600 font-extrabold tracking-tight uppercase text-[10px] mr-1.5 align-middle">{f.tipo === 'adesao' ? 'Adesão' : f.tipo === 'mensalidade' ? 'Mensalidade' : 'Avulsa'}</span>
                        {f.contrato?.metadata?.servico_nome || 'Serviço Proteção'} {f.contrato?.veiculo?.placa ? `— ${f.contrato.veiculo.placa}` : ''}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-1.5">
                            <Calendar size={12} className="text-surface-700/40" />
                            <p className="text-xs text-surface-700/60">
                                Vencimento: {formatDate(f.data_vencimento)}
                                {f.data_pagamento && ` • Pago: ${formatDate(f.data_pagamento)}`}
                            </p>
                        </div>
                        {/* Identificador de Empresa */}
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-surface-100 text-surface-500 uppercase tracking-tight">
                            {(f as any).asaas_empresa || 'ACSM'}
                        </span>
                    </div>
                </div>
            </button>

            {expanded && (
                <div className="border-t border-surface-100 p-4 bg-surface-50/50 space-y-3 animate-fade-in">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                            <p className="text-xs text-surface-700/60">Tipo</p>
                            <p className="font-medium text-surface-900 capitalize">
                                {f.tipo === 'mensalidade' ? 'Mensalidade' : f.tipo === 'adesao' ? 'Taxa de Adesão' : f.tipo}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-surface-700/60">Status</p>
                            <p className="font-medium text-surface-900 capitalize">{translateStatus(f.status)}</p>
                        </div>
                    </div>

                    {/* BOTÃO PRINCIPAL: FATURA ASAAS (Link da Página com Pix/Boleto/Cartão) */}
                    {(f.asaas_invoice_url || f.url_pix || (f.metadata as any)?.invoiceUrl || (f.metadata as any)?.asaas_invoice_url) ? (
                        <a href={f.asaas_invoice_url || f.url_pix || (f.metadata as any)?.invoiceUrl || (f.metadata as any)?.asaas_invoice_url} 
                            target="_blank" rel="noreferrer"
                            className="w-full flex items-center justify-center gap-3 p-4 rounded-2xl bg-surface-100 text-surface-900 font-bold text-sm hover:bg-surface-200 transition-all border border-surface-200 mb-3">
                            <ExternalLink size={18} /> VER FATURA NO ASAAS
                        </a>
                    ) : (
                        <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-center mb-3">
                            <p className="text-[10px] font-bold text-blue-700 uppercase">Sincronizando com Asaas...</p>
                        </div>
                    )}

                    {(!isPaga || isDeleted) && (
                        <div className="space-y-3">

                            <div className="grid grid-cols-2 gap-2">
                                {f.pix_copia_cola && (
                                    <button onClick={() => {
                                        navigator.clipboard.writeText(f.pix_copia_cola || '')
                                        setCopied(true)
                                        setTimeout(() => setCopied(false), 2000)
                                    }}
                                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-surface-100 text-surface-700 font-bold text-xs hover:bg-surface-200 transition-colors">
                                        {copied ? <Check size={14} /> : <Copy size={14} />} Pix
                                    </button>
                                )}

                                {f.url_boleto && (
                                    <a href={f.url_boleto} target="_blank" rel="noreferrer"
                                        className="flex items-center justify-center gap-2 p-3 rounded-xl bg-surface-100 text-surface-700 font-bold text-xs hover:bg-surface-200 transition-colors">
                                        <Receipt size={14} /> Boleto PDF
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
